import { Box, Button, FormikSelectInput, Modal, toast, Toggle } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useMemo } from 'react';
import * as yup from 'yup';

import {
  HoldbackPaymentBatchedSettingsFrequency,
  HoldbackPaymentBatchedSettingsFrequencyMap,
  DayOfWeek,
  OrdinalHoldbackPaymentSettings,
  HoldbackPaymentBatchedSettings,
  HoldbackPaymentBatchedSettingsCadenceMap,
  HoldbackPaymentBatchedSettingsCadence,
  DayNames,
} from 'types';

import getHoldbackScheduleLabel from '../../../../helpers/holdbackScheduleLabel';

interface HoldbackPaymentBatchedSettingsEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: any) => Promise<void>;
  currentSettings?: HoldbackPaymentBatchedSettings;
  modalId: string;
  modalTitle: string;
  submitButtonText?: string;
}

const frequencyOptions = Object.entries(HoldbackPaymentBatchedSettingsFrequencyMap).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const occurrenceOptions = Object.entries(OrdinalHoldbackPaymentSettings).map(([value, label]) => ({
  value: Number(value),
  label,
}));

export const HoldbackPaymentBatchedSettingsEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  currentSettings,
  modalId,
  modalTitle,
  submitButtonText = 'Save',
}: HoldbackPaymentBatchedSettingsEditModalProps) => {
  const initialValues = useMemo(() => {
    const payoutSchedule = currentSettings?.payoutSchedule;

    return {
      frequency: frequencyOptions.find((opt) => opt.value === payoutSchedule?.frequency) ?? frequencyOptions[0],
      day: payoutSchedule?.day ?? DayOfWeek.Friday,
      occurrence: occurrenceOptions.find((opt) => opt.value === payoutSchedule?.occurrences[0]),
      inherit: currentSettings?.inherit ?? false,
    };
  }, [currentSettings]);

  const validationSchema = yup.object().shape({
    frequency: yup.object().shape({
      value: yup.number().required(),
      label: yup.string().required(),
    }),
    day: yup.number().required(),
    occurrence: yup.object().when('frequency', {
      is: (freq: any) => freq?.value === HoldbackPaymentBatchedSettingsFrequency.oneTime,
      then: (schema) =>
        schema.shape({
          value: yup.number().min(1).required('Select a Friday'),
          label: yup.string().required(),
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
    inherit: yup.boolean(),
  });

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: { maxWidth: '500px', whiteSpace: 'pre-line', wordBreak: 'break-word' },
    });
  };

  const onSubmit = async (values: any, { setSubmitting, resetForm }: FormikHelpers<any>) => {
    try {
      // Calculate occurrences based on frequency
      let occurrences: number[] = [];
      const frequency = values.frequency.value as HoldbackPaymentBatchedSettingsFrequency;

      if (frequency === HoldbackPaymentBatchedSettingsFrequency.oneTime) {
        const occurrence = values.occurrence?.value;
        occurrences = [occurrence];
      }

      if (frequency === HoldbackPaymentBatchedSettingsFrequency.twoTimes) {
        occurrences = [1, 3];
      }

      if (frequency === HoldbackPaymentBatchedSettingsFrequency.fourTimes) {
        occurrences = [1, 2, 3, 4];
      }

      await handleSubmit({
        frequency,
        day: values.day,
        occurrences,
        inherit: values.inherit,
      });
      resetForm();
      handleClose();
      toast.success('Holdback payment settings updated successfully');
    } catch (error: any) {
      showErrorToast(`Failed to update holdback payment settings: ${error?.data?.message || String(error)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getScheduleMessage = (frequency: HoldbackPaymentBatchedSettingsFrequency, day: DayOfWeek) => {
    if (frequency === HoldbackPaymentBatchedSettingsFrequency.twoTimes) {
      return `on the ${getHoldbackScheduleLabel(frequency, day, [1, 3])}`;
    } else if (frequency === HoldbackPaymentBatchedSettingsFrequency.fourTimes) {
      return getHoldbackScheduleLabel(frequency, day, [1, 2, 3, 4]);
    }
    return null;
  };

  return (
    <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {({ isSubmitting, resetForm, submitForm, values, setFieldValue }) => {
        const scheduleMessage = getScheduleMessage(values.frequency.value, values.day);
        return (
          <Form>
            <Modal
              isOpen={isOpen}
              maxWidth="4xl"
              ariaLabelledBy={modalId}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            >
              <Modal.Header
                id={modalId}
                title={modalTitle}
                onDismiss={() => {
                  if (!isSubmitting) {
                    resetForm();
                    handleClose();
                  }
                }}
              />
              <Modal.Body background="secondary" childGap="lg">
                <Box childGap="lg">
                  <Box direction="row" justifyContent="space-between">
                    <Box width="40">
                      <Field
                        name="frequency"
                        label="Frequency"
                        component={FormikSelectInput}
                        options={frequencyOptions}
                        isDisabled={isSubmitting || values.inherit}
                      />
                    </Box>
                    <Box>
                      <Box fontWeight="medium" color="body-primary">
                        <Box margin="0 0 sm 0">Cadence</Box>
                        <Box height="md" alignItems="center" justifyContent="center">
                          {
                            HoldbackPaymentBatchedSettingsCadenceMap[
                              currentSettings?.payoutSchedule?.cadence ??
                                HoldbackPaymentBatchedSettingsCadence.monthly
                            ]
                          }
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {values.frequency.value === HoldbackPaymentBatchedSettingsFrequency.oneTime && (
                    <Box direction="row" justifyContent="space-between">
                      <Box width="40">
                        <Field
                          name="occurrence"
                          component={FormikSelectInput}
                          placeholder="Select a Friday"
                          options={occurrenceOptions}
                          isDisabled={isSubmitting || values.inherit}
                        />
                      </Box>
                      <Box fontWeight="medium" color="body-primary" justifyContent="center">
                        {DayNames[values.day]}
                      </Box>
                    </Box>
                  )}

                  {scheduleMessage && (
                    <Box>
                      <Box fontSize="sm" color="grey-700">
                        Automatically scheduled {scheduleMessage}
                      </Box>
                    </Box>
                  )}

                  <Toggle
                    id="inherit"
                    label="Inheriting from parent"
                    isChecked={values.inherit}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('inherit', e.target.checked);
                    }}
                    isDisabled={isSubmitting}
                  />
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  tone="neutral"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  type="submit"
                  onClick={submitForm}
                >
                  {submitButtonText}
                </Button>
              </Modal.Footer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
